import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { useToast } from '../../../lib/ToastContext'
import { useAIProcessing } from '../hooks/useAIProcessing'
import FragranceFields from './FragranceFields'
import { isFragrance, normalizeFragranceDraft, fragranceReview } from '../utils/fragranceDraft.mjs'

const ProductForm = ({ onSave, saving }) => {
  const [productFamily, setProductFamily] = useState('auto')
  const [fragrance, setFragrance] = useState({})
  const [image, setImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [description, setDescription] = useState('')
  const [costPrice, setCostPrice] = useState('')
  const [salePrice, setSalePrice] = useState('')
  const [compareAtPrice, setCompareAtPrice] = useState('')
  const [weight, setWeight] = useState('') // Weight in grams
  const [quantity, setQuantity] = useState('1')
  const [supplierName, setSupplierName] = useState('')
  const [supplierProductNumber, setSupplierProductNumber] = useState('')
  const [countryOfOrigin, setCountryOfOrigin] = useState('US')
  const [hsCode, setHsCode] = useState('')
  const [barcode, setBarcode] = useState('')
  const [isUnique, setIsUnique] = useState(true) // true = unique piece, false = can replicate
  const [aiData, setAiData] = useState(null)
  const [editingAi, setEditingAi] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [cameraModalOpen, setCameraModalOpen] = useState(false)
  const [cameraStarting, setCameraStarting] = useState(false)
  const [cameraError, setCameraError] = useState('')
  const [cameraAttempt, setCameraAttempt] = useState(0)

  const fileInputRef = useRef(null)
  const cameraVideoRef = useRef(null)
  const cameraStreamRef = useRef(null)
  const captureCanvasRef = useRef(null)
  const toast = useToast()
  const { processProduct, processing, error: aiError } = useAIProcessing()

  const isPerfume = productFamily === 'perfumes' || isFragrance(aiData || {})
  const changeFragrance = (key, value) => {
    const next = { ...fragrance, [key]: value }
    setFragrance(next)
    if (aiData && isPerfume) setAiData(normalizeFragranceDraft({ ...aiData, metafields: { ...aiData.metafields, ...next } }, { sellerDescription: description, priceInfo: { weight, fragrance: next } }))
  }

  // NOTE: the barcode is deliberately NOT derived from the SKU here. The old
  // derivation (first 7 SKU digits + recomputed checksum) fabricated a number
  // that matched nothing — and often collided with ANOTHER product's SKU.
  // Left empty, the onProductCreated trigger reserves a real unique EAN-8
  // from the registry and saves it to Shopify; or paste one from the
  // SKU/GTIN-8 generator tab.

  // Handle file selection
  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.warning('Please select an image file')
      return
    }

    setImage(file)

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setImagePreview(e.target.result)
    }
    reader.readAsDataURL(file)

    // Reset AI data when new image is uploaded
    setAiData(null)
    setEditingAi(false)
  }, [toast])

  const stopCameraStream = useCallback(() => {
    const stream = cameraStreamRef.current
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      cameraStreamRef.current = null
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null
    }
  }, [])

  const handleOpenCamera = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator?.mediaDevices?.getUserMedia) {
      toast.warning('Camera is not available in this browser')
      return
    }
    setCameraError('')
    setCameraAttempt((prev) => prev + 1)
    setCameraModalOpen(true)
  }, [toast])

  const handleCloseCamera = useCallback(() => {
    setCameraModalOpen(false)
    setCameraError('')
    stopCameraStream()
  }, [stopCameraStream])

  const handleRetryCamera = useCallback(() => {
    setCameraError('')
    setCameraAttempt((prev) => prev + 1)
  }, [])

  const handleCapturePhoto = useCallback(() => {
    const video = cameraVideoRef.current
    const canvas = captureCanvasRef.current
    if (!video || !canvas || video.readyState < 2) {
      toast.warning('Camera is still loading. Please wait a moment and try again.')
      return
    }

    const width = video.videoWidth || 1280
    const height = video.videoHeight || 720
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      toast.error('Failed to capture image from camera')
      return
    }

    ctx.drawImage(video, 0, 0, width, height)
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error('Failed to generate image file from camera capture')
        return
      }
      const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' })
      handleFile(file)
      handleCloseCamera()
    }, 'image/jpeg', 0.92)
  }, [handleCloseCamera, handleFile, toast])

  useEffect(() => {
    if (!cameraModalOpen) return undefined

    let cancelled = false

    const startCamera = async () => {
      setCameraStarting(true)
      stopCameraStream()
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        })

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        cameraStreamRef.current = stream
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream
          await cameraVideoRef.current.play().catch(() => {})
        }
      } catch (err) {
        const denied = err?.name === 'NotAllowedError' || err?.name === 'SecurityError'
        const noDevice = err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError'
        const message = denied
          ? 'Camera permission denied. Please allow camera access and try again.'
          : noDevice
          ? 'No camera device found on this computer.'
          : (err?.message || 'Could not open camera')
        setCameraError(message)
      } finally {
        if (!cancelled) {
          setCameraStarting(false)
        }
      }
    }

    startCamera()
    return () => {
      cancelled = true
      stopCameraStream()
    }
  }, [cameraModalOpen, cameraAttempt, stopCameraStream])

  useEffect(() => {
    return () => stopCameraStream()
  }, [stopCameraStream])

  // Handle drag and drop
  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }, [handleFile])

  // Process with AI
  const handleAIProcess = async () => {
    if (!imagePreview || !description.trim()) {
      toast.warning('Please upload an image and enter a description')
      return
    }

    if (!costPrice || !salePrice) {
      toast.warning('Please enter cost and sale prices')
      return
    }

    const result = await processProduct(imagePreview, description, {
      costPrice: parseFloat(costPrice),
      salePrice: parseFloat(salePrice),
      compareAtPrice: compareAtPrice ? parseFloat(compareAtPrice) : null,
      weight: weight ? parseFloat(weight) : null, productFamily, fragrance
    })

    if (result) {
      setAiData(result)
      if (isFragrance(result)) { setFragrance(result.metafields); setIsUnique(false); setCountryOfOrigin('') }
    }
  }

  // Handle AI data edit
  const handleAiDataChange = (field, value) => {
    setAiData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleMetafieldChange = (field, value) => {
    setAiData(prev => ({
      ...prev,
      metafields: {
        ...(prev?.metafields || {}),
        [field]: value
      }
    }))
  }

  const handleWeightChange = (value) => {
    setWeight(value)
    setAiData(prev => {
      if (!prev) return prev
      return {
        ...prev,
        weight: value,
        metafields: {
          ...(prev?.metafields || {}),
          weight: value
        }
      }
    })
  }

  const reviewItems = useMemo(
    () => Array.isArray(aiData?.reviewFlags) ? aiData.reviewFlags.filter(Boolean) : [],
    [aiData]
  )

  const missingFields = useMemo(
    () => Array.isArray(aiData?.missingFields) ? aiData.missingFields.filter(Boolean) : [],
    [aiData]
  )

  // Save product
  const handleSave = async () => {
    if (!image || !aiData) {
      toast.warning('Please complete all steps first')
      return
    }

    if (isPerfume && fragranceReview(aiData.metafields).some(item => item.severity === 'required')) { toast.warning('Confirm perfume brand, fragrance name and bottle volume before publishing'); return }
    const resolvedWeightText = String(weight || aiData.weight || aiData.metafields?.weight || '').trim()
    const resolvedWeight = resolvedWeightText ? parseFloat(resolvedWeightText) : null
    const resolvedBarcode = String(barcode || '').trim()
    const resolvedMetafields = {
      ...(aiData.metafields || {}),
      size: String(aiData.metafields?.size || aiData.metafields?.ring_size || '').trim(),
      weight: resolvedWeightText,
      weight_unit: resolvedWeightText
        ? String(aiData.metafields?.weight_unit || 'g').trim() || 'g'
        : String(aiData.metafields?.weight_unit || '').trim(),
      gtin: String((isPerfume && resolvedBarcode) || aiData.metafields?.gtin || resolvedBarcode).trim(),
      mpn: String(aiData.metafields?.mpn || (isPerfume ? '' : supplierProductNumber || aiData.sku) || '').trim(),
      supplier_name: String(supplierName || '').trim(),
      supplier_product_number: String(supplierProductNumber || '').trim()
    }

    const productData = {
      title: aiData.title,
      description: aiData.description,
      metaTitle: aiData.metaTitle,
      metaDescription: aiData.metaDescription,
      urlSlug: aiData.urlSlug,
      tags: aiData.tags,
      category: aiData.category,
      subcategory: aiData.subcategory,
      productType: aiData.productType,
      sku: aiData.sku,
      metafields: resolvedMetafields,
      detectedInfo: aiData.detectedInfo,
      reviewFlags: reviewItems,
      missingFields,
      costPrice: parseFloat(costPrice),
      salePrice: parseFloat(salePrice),
      compareAtPrice: compareAtPrice ? parseFloat(compareAtPrice) : null,
      weight: resolvedWeight,
      originalDescription: description,
      isUnique, // true = unique piece, false = can replicate
      supplierName: supplierName || null,
      supplierProductNumber: supplierProductNumber || null,
      countryOfOrigin: countryOfOrigin || (isPerfume ? null : 'US'),
      hsCode: hsCode || null,
      barcode: barcode || (isPerfume ? aiData.metafields?.gtin : null) || null,
      ...(isPerfume && (barcode || aiData.metafields?.gtin) ? { barcodeSource: 'manufacturer' } : {}),
      inventory: {
        quantity: Math.max(0, parseInt(quantity || '0', 10) || 0),
        trackInventory: true,
        continueSellingWhenOutOfStock: isPerfume ? false : !isUnique // Only continue if can replicate
      }
    }

    try {
      await onSave(productData, image)

      // Reset form
      setProductFamily('auto'); setFragrance({})
      setImage(null)
      setImagePreview(null)
      setDescription('')
      setCostPrice('')
      setSalePrice('')
      setCompareAtPrice('')
      setWeight('')
      setQuantity('1')
      setSupplierName('')
      setSupplierProductNumber('')
      setCountryOfOrigin('US')
      setHsCode('')
      setBarcode('')
      setIsUnique(true)
      setAiData(null)
      setEditingAi(false)
    } catch (err) {
      toast.error('Failed to save product: ' + err.message)
    }
  }

  // Clear form
  const handleClear = () => {
    setProductFamily('auto'); setFragrance({})
    setImage(null)
    setImagePreview(null)
    setDescription('')
    setCostPrice('')
    setSalePrice('')
    setCompareAtPrice('')
    setWeight('')
    setQuantity('1')
    setSupplierName('')
    setSupplierProductNumber('')
    setCountryOfOrigin('US')
    setHsCode('')
    setBarcode('')
    setIsUnique(true)
    setAiData(null)
    setEditingAi(false)
    handleCloseCamera()
  }

  return (
    <Card variant="solid" className="h-full border border-border/60 shadow-elevation-1">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground">Create Product</h3>
        {(image || description) && (
          <Button variant="ghost" size="sm" onClick={handleClear}>
            <Icon name="X" size={16} className="mr-1" />
            Clear
          </Button>
        )}
      </div>

      <div className="space-y-4 [&_input]:bg-input [&_input]:text-foreground [&_input]:placeholder:text-muted-foreground [&_input:disabled]:bg-muted/50 [&_input:disabled]:text-muted-foreground [&_input:disabled]:cursor-not-allowed [&_textarea]:bg-input [&_textarea]:text-foreground [&_textarea]:placeholder:text-muted-foreground [&_select]:bg-input [&_select]:text-foreground">
        <label className="block text-sm font-medium text-foreground">Product category<select aria-label="Product category" className="mt-1 w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-foreground" value={productFamily} onChange={event => { const next=event.target.value; setProductFamily(next); setAiData(null); setEditingAi(false); if(next==='perfumes'){setIsUnique(false);setCountryOfOrigin('')} }}><option value="auto">Detect from photo / description</option><option value="jewelry">Jewelry</option><option value="perfumes">Perfume / High perfumery</option></select></label>
        {isPerfume && <><FragranceFields value={fragrance} onChange={changeFragrance} /><label className="block text-sm font-medium text-foreground">Manufacturer barcode (UPC/EAN, optional)<input className="mt-1 w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-foreground" inputMode="numeric" value={barcode} onChange={event=>setBarcode(event.target.value.replace(/\D/g,'').slice(0,14))} placeholder="Copy from the original packaging" /></label></>}
        {/* Step 1: Image Upload */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            1. Photo (white background)
          </label>

          <div
            className={`relative border-2 border-dashed rounded-xl transition-colors ${
              dragActive
                ? 'border-primary bg-primary/5'
                : imagePreview
                ? 'border-primary/30 bg-primary/5'
                : 'border-border/60 hover:border-primary/40'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {imagePreview ? (
              <div className="relative aspect-square max-h-64 mx-auto">
                <img
                  src={imagePreview}
                  alt="Product preview"
                  className="w-full h-full object-contain rounded-lg"
                />
                <button
                  onClick={() => {
                    setImage(null)
                    setImagePreview(null)
                    setAiData(null)
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-background/80 rounded-full shadow-lg shadow-black/20 hover:bg-muted/50"
                >
                  <Icon name="X" size={16} className="text-muted-foreground" />
                </button>
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center py-8 cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon name="Upload" size={32} className="text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-1">
                  Drop image here or click to upload
                </p>
                <p className="text-xs text-muted-foreground">
                  White background recommended
                </p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
            />
          </div>

          <div className="mt-3 hidden md:flex items-center justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconName="Camera"
              onClick={handleOpenCamera}
            >
              Use Camera (Desktop)
            </Button>
          </div>
        </div>

        {/* Step 2: Description */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            2. Description (EN or PT)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={isPerfume ? 'Brand, fragrance name, EDP/EDT, 100 mL, sealed box…' : 'Ex: Anel masculino com onyx de 3ct pesando 3.5g, 14k gold…'}
            rows={3}
            maxLength={1000}
            className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
          />
        </div>

        {/* Step 3: Prices, Inventory & Supplier */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            3. Prices, Inventory & Supplier
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Cost Price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={costPrice}
                  onChange={(e) => setCostPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2 border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Sale Price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2 border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{isPerfume ? 'Shipping weight (grams, optional)' : 'Weight (grams)'}</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={weight}
                  onChange={(e) => handleWeightChange(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">g</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Compare At (optional)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={compareAtPrice}
                  onChange={(e) => setCompareAtPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2 border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Quantity in Stock</label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                min="0"
                className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Supplier Name</label>
              <input
                type="text"
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder={isPerfume ? 'Distributor / supplier' : 'Vendor / Supplier'}
                className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div className="col-span-1 md:col-span-2">
              <label className="text-xs text-muted-foreground">Supplier Product #</label>
              <input
                type="text"
                value={supplierProductNumber}
                onChange={(e) => setSupplierProductNumber(e.target.value)}
                placeholder="Supplier product number / model"
                className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>
        </div>

        {/* Step 4: Unique or Replicable */}
        {!isPerfume && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            4. Inventory Type
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setIsUnique(true)}
              className={`p-3 rounded-xl border-2 text-left transition-all ${
                isUnique
                  ? 'border-primary bg-primary/5'
                  : 'border-border/60 hover:border-primary/30'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon
                  name="Gem"
                  size={18}
                  className={isUnique ? 'text-primary' : 'text-muted-foreground'}
                />
                <span className={`font-medium text-sm ${isUnique ? 'text-primary' : 'text-foreground'}`}>
                  Unique Piece
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                One-of-a-kind, cannot be replicated
              </p>
            </button>

            <button
              type="button"
              onClick={() => setIsUnique(false)}
              className={`p-3 rounded-xl border-2 text-left transition-all ${
                !isUnique
                  ? 'border-primary bg-primary/5'
                  : 'border-border/60 hover:border-primary/30'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon
                  name="Copy"
                  size={18}
                  className={!isUnique ? 'text-primary' : 'text-muted-foreground'}
                />
                <span className={`font-medium text-sm ${!isUnique ? 'text-primary' : 'text-foreground'}`}>
                  Can Replicate
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Can be made again if sold
              </p>
            </button>
          </div>
        </div>
        )}

        {/* AI Process Button */}
        {!aiData && (
          <Button
            variant="outline"
            className="w-full"
            onClick={handleAIProcess}
            disabled={!imagePreview || !description.trim() || !costPrice || !salePrice || processing}
            iconName={processing ? 'Loader2' : aiError ? 'RefreshCw' : 'Sparkles'}
            iconPosition="left"
          >
            {processing ? 'Generating Product Details...' : aiError ? 'Try Again With Same Info' : 'Generate with AI'}
          </Button>
        )}

        {aiError && (
          <div className="p-3 bg-warning/15 border border-warning/30 rounded-lg text-sm text-warning">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-warning">Claude could not generate product details.</p>
                <p className="mt-1">{aiError}</p>
                {!aiData && (
                  <p className="mt-2 text-xs text-warning/90">
                    Your photo and form information were kept. Try again with the same information.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* AI Generated Content */}
        {aiData && (
          <div className="space-y-4 pt-3 border-t border-border/60">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-foreground flex items-center gap-2">
                <Icon name="Sparkles" size={16} className="text-primary" />
                Review & Edit
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingAi(!editingAi)}
              >
                {editingAi ? 'Done' : 'Edit'}
              </Button>
            </div>

            {reviewItems.length > 0 && (
              <div className="p-4 border border-warning/30 rounded-xl bg-warning/10">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Icon name="ClipboardList" size={15} className="text-warning" />
                    <h5 className="text-sm font-semibold text-foreground">Review Before Save</h5>
                  </div>
                  <span className="text-xs font-medium text-warning">
                    {reviewItems.length} item{reviewItems.length === 1 ? '' : 's'}
                  </span>
                </div>

                <div className="mt-3 space-y-2">
                  {reviewItems.map((item, index) => (
                    <div
                      key={`${item.field || 'review'}-${index}`}
                      className="flex items-start justify-between gap-3 rounded-lg border border-warning/20 bg-background/80 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {item.label || item.field || 'Manual review'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {item.reason || 'Needs manual review.'}
                        </p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        item.severity === 'required'
                          ? 'bg-destructive/12 text-destructive'
                          : 'bg-warning/15 text-warning'
                      }`}>
                        {item.severity === 'required' ? 'Required' : 'Recommended'}
                      </span>
                    </div>
                  ))}
                </div>

                {missingFields.length > 0 && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Unresolved fields: {missingFields.join(', ')}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left: Vital Info */}
              <div className="p-4 border border-border/60 rounded-xl bg-background/80 space-y-3">
                <div className="flex items-center gap-2">
                  <Icon name="ListChecks" size={14} className="text-muted-foreground" />
                  <h5 className="text-sm font-semibold text-foreground">Informações vitais</h5>
                </div>

                {/* Title */}
                <div>
                  <label className="text-xs text-muted-foreground">Title</label>
                  {editingAi ? (
                    <input
                      type="text"
                      value={aiData.title}
                      onChange={(e) => handleAiDataChange('title', e.target.value)}
                      className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                    />
                  ) : (
                    <p className="font-medium text-foreground">{aiData.title}</p>
                  )}
                </div>

                {/* SKU & Product Type */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">SKU</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.sku || ''}
                        onChange={(e) => handleAiDataChange('sku', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm font-mono"
                      />
                    ) : (
                      <p className="text-sm text-foreground font-mono">{aiData.sku || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Product Type (Label)</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.productType || ''}
                        onChange={(e) => handleAiDataChange('productType', e.target.value)}
                        maxLength={20}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.productType || 'Jewelry'}</p>
                    )}
                  </div>
                </div>

                {/* Prices & Quantity */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Sale Price</label>
                    {editingAi ? (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <input
                          type="number"
                          value={salePrice}
                          onChange={(e) => setSalePrice(e.target.value)}
                          placeholder="0.00"
                          className="w-full pl-7 pr-3 py-2 border border-border/60 rounded-lg text-sm"
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-foreground">{salePrice ? `$${salePrice}` : '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Compare At</label>
                    {editingAi ? (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <input
                          type="number"
                          value={compareAtPrice}
                          onChange={(e) => setCompareAtPrice(e.target.value)}
                          placeholder="0.00"
                          className="w-full pl-7 pr-3 py-2 border border-border/60 rounded-lg text-sm"
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-foreground">{compareAtPrice ? `$${compareAtPrice}` : '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Quantity</label>
                    {editingAi ? (
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        min="0"
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{quantity || '0'}</p>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs text-muted-foreground">Description (HTML)</label>
                  {editingAi ? (
                    <textarea
                      value={aiData.description}
                      onChange={(e) => handleAiDataChange('description', e.target.value)}
                      rows={5}
                      className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm resize-none"
                    />
                  ) : (
                    <div
                      className="mt-1 p-3 bg-muted/30 rounded-lg text-sm text-muted-foreground max-h-40 overflow-y-auto prose prose-sm"
                      dangerouslySetInnerHTML={{ __html: aiData.description || '' }}
                    />
                  )}
                </div>

                {/* Meta Title */}
                <div>
                  <label className="text-xs text-muted-foreground">Meta Title</label>
                  {editingAi ? (
                    <input
                      type="text"
                      value={aiData.metaTitle}
                      onChange={(e) => handleAiDataChange('metaTitle', e.target.value)}
                      className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">{aiData.metaTitle}</p>
                  )}
                </div>

                {/* Meta Description */}
                <div>
                  <label className="text-xs text-muted-foreground">
                    Meta Description ({aiData.metaDescription?.length || 0}/160)
                  </label>
                  {editingAi ? (
                    <textarea
                      value={aiData.metaDescription}
                      onChange={(e) => handleAiDataChange('metaDescription', e.target.value)}
                      rows={2}
                      maxLength={160}
                      className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm resize-none"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">{aiData.metaDescription}</p>
                  )}
                </div>

                {/* URL Slug */}
                <div>
                  <label className="text-xs text-muted-foreground">URL Slug</label>
                  {editingAi ? (
                    <input
                      type="text"
                      value={aiData.urlSlug}
                      onChange={(e) => handleAiDataChange('urlSlug', e.target.value)}
                      className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">/products/{aiData.urlSlug}</p>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <label className="text-xs text-muted-foreground">Tags</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {aiData.tags?.length ? aiData.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 bg-muted/50 border border-border/60 rounded-full text-xs text-muted-foreground"
                    >
                      {tag}
                    </span>
                  )) : (
                    <span className="text-xs text-muted-foreground">No tags</span>
                  )}
                </div>
                </div>

                {/* Category */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Category</label>
                    <p className="text-sm text-foreground capitalize">{aiData.category || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Subcategory</label>
                    <p className="text-sm text-foreground capitalize">{aiData.subcategory || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Right: Hidden Shopify Fields */}
              {!isPerfume && <div className="p-4 border border-border/60 rounded-xl bg-background/80 space-y-3">
                <div className="flex items-center gap-2">
                  <Icon name="EyeOff" size={14} className="text-muted-foreground" />
                  <h5 className="text-sm font-semibold text-foreground">Campos ocultos (Shopify)</h5>
                </div>
                <p className="text-xs text-muted-foreground">
                  Esses campos são salvos como metafields e não aparecem na página pública.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Material</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.material || ''}
                        onChange={(e) => handleMetafieldChange('material', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.material || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Karat</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.karat || ''}
                        onChange={(e) => handleMetafieldChange('karat', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.karat || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Weight (g)</label>
                    {editingAi ? (
                      <input
                        type="number"
                        step="0.01"
                        value={weight || aiData.weight || aiData.metafields?.weight || ''}
                        onChange={(e) => handleWeightChange(e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">
                        {weight || aiData.weight || aiData.metafields?.weight ? `${weight || aiData.weight || aiData.metafields?.weight}g` : '-'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Weight Unit</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.weight_unit || ''}
                        onChange={(e) => handleMetafieldChange('weight_unit', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.weight_unit || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Gender</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.gender || ''}
                        onChange={(e) => handleMetafieldChange('gender', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.gender || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Age Group</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.age_group || ''}
                        onChange={(e) => handleMetafieldChange('age_group', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.age_group || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Color</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.color || ''}
                        onChange={(e) => handleMetafieldChange('color', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.color || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Size</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.size || ''}
                        onChange={(e) => handleMetafieldChange('size', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.size || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Condition</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.condition || ''}
                        onChange={(e) => handleMetafieldChange('condition', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.condition || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">GTIN</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.gtin || ''}
                        onChange={(e) => handleMetafieldChange('gtin', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.gtin || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">MPN</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.mpn || ''}
                        onChange={(e) => handleMetafieldChange('mpn', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.mpn || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Stone Type</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.stone_type || ''}
                        onChange={(e) => handleMetafieldChange('stone_type', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.stone_type || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Stone Carat</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.stone_carat || ''}
                        onChange={(e) => handleMetafieldChange('stone_carat', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.stone_carat || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Ring Size</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={aiData.metafields?.ring_size || ''}
                        onChange={(e) => handleMetafieldChange('ring_size', e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{aiData.metafields?.ring_size || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Supplier Name</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={supplierName}
                        onChange={(e) => setSupplierName(e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{supplierName || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Supplier Product #</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={supplierProductNumber}
                        onChange={(e) => setSupplierProductNumber(e.target.value)}
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{supplierProductNumber || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Country of Origin</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={countryOfOrigin}
                        onChange={(e) => setCountryOfOrigin(e.target.value.toUpperCase())}
                        placeholder="US"
                        maxLength={2}
                        disabled
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm uppercase bg-muted/50 text-muted-foreground cursor-not-allowed"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{countryOfOrigin || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">HS Code</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={hsCode}
                        onChange={(e) => setHsCode(e.target.value)}
                        placeholder="7113.19"
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{hsCode || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Barcode (EAN‑8)</label>
                    {editingAi ? (
                      <input
                        type="text"
                        value={barcode}
                        onChange={(e) => setBarcode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                        placeholder="Auto"
                        className="w-full px-3 py-2 border border-border/60 rounded-lg text-sm font-mono"
                      />
                    ) : (
                      <p className="text-sm text-foreground font-mono">{barcode || '-'}</p>
                    )}
                  </div>
                </div>
              </div>}
            </div>

            {/* Print availability */}
            <div className="pt-2 border-t border-border/60">
              <p className="text-xs text-muted-foreground text-center">
                Label print is available after publish in the Update tab.
              </p>
            </div>
          </div>
        )}

        {/* Save Button */}
        {aiData && (
          <Button
            variant="default"
            className="w-full py-3"
            onClick={handleSave}
            disabled={saving}
            iconName={saving ? 'Loader2' : 'Check'}
            iconPosition="left"
          >
            {saving ? 'Saving...' : 'Save & Publish to POS'}
          </Button>
        )}

        {/* Info Note */}
        <div className="p-3 bg-muted/40 border border-border/60 rounded-lg text-xs text-muted-foreground">
          <Icon name="Info" size={14} className="inline mr-1" />
          Product will be published to <strong>POS only</strong>.
        </div>
      </div>

      {cameraModalOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm p-4 flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCloseCamera()
          }}
        >
          <div className="w-full max-w-4xl bg-card border border-border/60 rounded-2xl shadow-elevation-3 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
              <div>
                <p className="text-sm font-semibold text-foreground">Capture Product Photo</p>
                <p className="text-xs text-muted-foreground">Desktop camera capture for Product Listing</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={handleCloseCamera}>
                <Icon name="X" size={16} />
              </Button>
            </div>

            <div className="p-4">
              <div className="relative rounded-xl overflow-hidden border border-border/60 bg-black aspect-video">
                <video
                  ref={cameraVideoRef}
                  autoPlay
                  muted
                  playsInline
                  className={`w-full h-full object-contain ${cameraError ? 'hidden' : 'block'}`}
                />

                {cameraStarting && !cameraError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-sm">
                    <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                    Starting camera...
                  </div>
                )}

                {cameraError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                    <Icon name="AlertTriangle" size={18} className="text-warning mb-2" />
                    <p className="text-sm text-white">{cameraError}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-4 py-3 border-t border-border/60 flex flex-wrap items-center justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={handleCloseCamera}>
                Cancel
              </Button>
              {cameraError ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={handleRetryCamera}
                >
                  Retry Camera
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  iconName="Camera"
                  onClick={handleCapturePhoto}
                  disabled={cameraStarting}
                >
                  Capture Photo
                </Button>
              )}
            </div>
          </div>
          <canvas ref={captureCanvasRef} className="hidden" />
        </div>
      )}
    </Card>
  )
}

export default ProductForm
